import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, ArrowLeft, Search, HelpCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

export default function NotFound() {
  const navigate = useNavigate();
  const { user, roles } = useAuth();

  const getDashboardRoute = () => {
    if (!user) return "/";
    if (roles.includes("admin")) return "/admin/dashboard";
    if (roles.includes("supplier")) return "/supplier/dashboard";
    return "/buyer/dashboard";
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-2xl">
        <CardContent className="py-12 text-center">
          {/* Illustration */}
          <div className="mb-8">
            <div className="mx-auto flex size-48 shrink-0 items-center justify-center rounded-full bg-muted/30">
              <div className="text-center">
                <p className="text-7xl font-bold text-primary">404</p>
                <Search className="mx-auto mt-2 size-12 text-muted-foreground" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="mb-4 text-4xl font-bold">Page Not Found</h1>
          
          {/* Description */}
          <p className="mx-auto mb-8 max-w-md text-lg text-muted-foreground">
            Oops! The page you're looking for doesn't exist. It might have been moved or deleted.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              size="lg"
              onClick={() => navigate(getDashboardRoute())}
              className="min-w-[200px]"
            >
              <Home className="mr-2 size-5" />
              {user ? "Go to Dashboard" : "Go Home"}
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(-1)}
              className="min-w-[200px]"
            >
              <ArrowLeft className="mr-2 size-5" />
              Go Back
            </Button>
          </div>

          {/* Helpful Links */}
          <div className="mt-12 border-t pt-8">
            <p className="mb-4 text-sm text-muted-foreground">Need help? Try these:</p>
            <div className="flex flex-wrap justify-center gap-4">
              {user ? (
                <>
                  {roles.includes("buyer") && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/buyer/rfq/new")}
                      >
                        Post an RFQ
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(user ? "/buyer/suppliers" : "/auth?redirect=/buyer/suppliers")}
                      >
                        Find Suppliers
                      </Button>
                    </>
                  )}
                  {roles.includes("supplier") && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/supplier/rfq-marketplace")}
                      >
                        Browse RFQs
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/supplier/products")}
                      >
                        My Products
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/buyer/support")}
                  >
                    <HelpCircle className="mr-1 size-4" />
                    Contact Support
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/how-it-works")}
                  >
                    How It Works
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/for-buyers")}
                  >
                    For Buyers
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/for-suppliers")}
                  >
                    For Suppliers
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/contact")}
                  >
                    <HelpCircle className="mr-1 size-4" />
                    Contact Us
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
